import { expVal, expValEquals } from '@confluence/feature-experiments';
import type {
	ReactionsConfluenceSaveReactionMutation,
	ReactionsDeleteReactionMutation,
	ReactionsMutationResultsData,
} from '@confluence/reactions/entry-points/reactionTypes.ts';
import { CommentType } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import type { EditCommentUpdateCommentMutation_Mutation } from '../__types__/EditCommentUpdateCommentMutation.ts';

// Function overloads for better type safety
export function getOptimisticResponse(params: {
	ari: string;
	containerAri: string;
	emojiId: string;
	action: 'add';
}): ReactionsConfluenceSaveReactionMutation | undefined;

export function getOptimisticResponse(params: {
	ari: string;
	containerAri: string;
	emojiId: string;
	action: 'delete';
}): ReactionsDeleteReactionMutation | undefined;

export function getOptimisticResponse({
	ari,
	containerAri,
	emojiId,
	action,
}: {
	ari: string;
	containerAri: string;
	emojiId: string;
	action: 'add' | 'delete';
}): ReactionsMutationResultsData | undefined {
	if (!expValEquals('confluence_comments_tuneup', 'isEnabled', true)) {
		return undefined;
	}

	if (action === 'add') {
		return {
			__typename: 'Mutation',
			confluence_addReaction: {
				__typename: 'ConfluenceReactionPayload',
				ari,
				containerAri,
				emojiId,
			},
		} as ReactionsConfluenceSaveReactionMutation;
	} else {
		return {
			__typename: 'Mutation',
			deleteReaction: {
				__typename: 'SaveReactionResponse',
				ari,
				containerAri,
				emojiId,
			},
		} as ReactionsDeleteReactionMutation;
	}
}

/**
 * Builds an optimistic response for EditCommentUpdateCommentMutation so that the comment body,
 * version timestamp, and author are updated in the cache immediately when a user saves an inline
 * comment edit — making the editor close instantly and showing the "(edited)" label before the
 * network round-trip completes.
 *
 * NOTE: version.number is set to 2 so that (version.number || 0) > 1 evaluates to true,
 * which renders the "(edited)" label immediately. The real version.number is written by the
 * cache update callback once the server responds.
 *
 * Returns undefined when:
 * - the comment is a general (footer) comment — only inline comments are supported
 * - cc_comments_new_comments_data_manager is disabled
 * - confluence_fe_edit_comment_optimistic is disabled
 *
 * NOTE: Moving this to @confluence/comments-common is deferred — there is a circular dependency
 * (comments-common -> inline-comments-common) that prevents the move without a larger refactor.
 */
export function getEditCommentOptimisticResponse({
	commentId,
	commentType,
	adfString,
	sessionDisplayName,
}: {
	commentId: string;
	commentType: CommentType;
	adfString: string;
	sessionDisplayName: string | null;
}): EditCommentUpdateCommentMutation_Mutation | undefined {
	if (
		commentType === CommentType.GENERAL ||
		!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false) ||
		!expVal('confluence_fe_edit_comment_optimistic', 'isEnabled', false)
	) {
		return undefined;
	}

	return {
		updateComment: {
			id: commentId,
			body: {
				value: adfString,
			},
			version: {
				number: 2,
				when: new Date().toISOString(),
				by: {
					displayName: sessionDisplayName,
				},
			},
		},
	};
}
